import { createMachine, assign } from "xstate";
import { makeTableColumns, makeTableData } from "./helpers/initData";
import downloadSvg, { downloadPng } from "svg-crowbar";

const demoBins = ["0-1", "1-2", "2-3", "3-4", "4-5", "5-6", "6-7", "7+"];
const demoDirections = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "E",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSW",
  "SW",
  "WSW",
  "W",
  "WNW",
  "NW",
  "NNW",
];

// directions and bins
const defineOwnDataMachine = createMachine(
  {
    initial: "selectDirections",
    context: {
      directions: [],
      bins: [],
    },
    states: {
      selectDirections: {
        initial: "cardinal",
        states: {
          cardinal: {
            on: {
              TOGGLE_CARDINAL: "degrees",
            },
          },
          degrees: {
            on: {
              TOGGLE_CARDINAL: "cardinal",
            },
          },
          hist: {
            type: "history",
            history: "deep",
          },
        },
        on: {
          SET_DIRECTIONS: {
            actions: "setDirections",
          },
          CONFIRM_DIRECTIONS: {
            target: "selectBins",
            cond: (context) => context.directions.length,
          },
        },
      },
      selectBins: {
        initial: "uniform",
        states: {
          uniform: {
            on: {
              TOGGLE_UNIFORM: "nonUniform",
            },
          },
          nonUniform: {
            on: {
              TOGGLE_UNIFORM: "uniform",
            },
          },
          hist: {
            type: history,
          },
        },
        on: {
          SET_BINS: {
            actions: "setBins",
            cond: (context, event) => event.bins.length < 10,
          },
          CONFIRM_BINS: {
            target: "confirmOwnData",
            cond: (context) => context.bins.length,
          },
          GO_BACK_DIRECTIONS: {
            target: "selectDirections",
          },
        },
      },
      confirmOwnData: {
        on: {
          GO_BACK_BINS: "selectBins",
          CONFIRM_OWN_DATA: {
            target: "endDirectionsAndBins",
            cond: (context) =>
              context.bins.length > 0 && context.directions.length > 0,
          },
        },
      },
      endDirectionsAndBins: {
        type: "final",
        data: (context) => ({
          bins: context.bins,
          directions: context.directions,
        }),
      },
    },
  },
  {
    actions: {
      setDirections: assign({
        directions: (context, event) => event.directions,
      }),
      setBins: assign({
        bins: (context, event) => event.bins,
      }),
    },
  }
);

const windroseDataMachine = createMachine(
  {
    id: "windroseData",
    initial: "selectInitData",
    context: {
      tableData: [],
      tableColumns: [],
      binUnits: "",
      binsTitle: "",
      // colorScheme: "blues",
      directions: [],
      bins: [],
    },
    states: {
      selectInitData: {
        initial: "useExample", // "defineData",
        id: "selectInitData",
        on: {
          DEFINE_DATA: "selectInitData.defineData",
          UPLOAD_FILE: "selectInitData.uploadFile",
          LOAD_PREV_DATA: "selectInitData.loadPrevious",
          SHOW_EXAMPLE: "selectInitData.useExample",
        },
        states: {
          defineData: {
            invoke: {
              id: "defineOwnData",
              src: defineOwnDataMachine,
              onDone: {
                target: "#windroseData.enterMetadata",
                actions: [
                  "setBinsData",
                  "setDirectionsData",
                  "makeTableDataAndColumnsData",
                ],
              },
            },
          },
          useExample: {
            initial: "selectExample",
            states: {
              selectExample: {
                on: {
                  CONFIRM_EXAMPLE_DATA: {
                    target: "#windroseData.editWindrose",
                  },
                },
              },
            },
            entry: "setDemoData",
          },
          loadPrevious: {
            initial: "selectPrevData",
            states: {
              selectPrevData: {
                on: {
                  SET_PREV_DATA: {
                    actions: [
                      "setBins",
                      // "setDirections", is in tableData
                      "setYUnits",
                      "setBinsTitle",
                      "setUnits",
                      "makeTableColumns",
                      "setTableData",
                    ],
                  },
                  CONFIRM_PREV_DATA: {
                    target: "#windroseData.editWindrose",
                  },
                },
              },
            },
          },
          uploadFile: {
            initial: "selectFile",
            states: {
              selectFile: {
                on: {
                  SET_FILE_DATA: {
                    actions: ["setBins", "makeTableDataAndColumns"],
                  },
                  CONFIRM_FILE: {
                    target: "#windroseData.enterMetadata",
                    cond: (context) => context.bins.length,
                  },
                },
              },
            },
          },
          hist: {
            type: "history",
            history: "deep",
          },
        },
      },
      enterMetadata: {
        on: {
          SET_UNITS: {
            actions: "setUnits",
          },
          SET_BINS_TITLE: {
            actions: "setBinsTitle",
          },
          SET_Y_UNITS: {
            actions: "setYUnits",
          },
          GO_BACK: "selectInitData.hist",
          CONFIRM_METADATA: {
            target: "#windroseData.editWindrose",
          },
        },
      },
      editWindrose: {
        initial: "editing",
        states: {
          donationPopup: {
            on: {
              NAH: "editing",
            },
          },
          editing: {},
        },
        on: {
          DOWNLOAD_SVG: {
            target: ".donationPopup",
            actions: "downloadWindroseSvg",
          },
          DOWNLOAD_PNG: {
            target: ".donationPopup",
            actions: "downloadWindrosePng",
          },
        },
      },
    },
  },
  {
    actions: {
      setDirections: assign({
        directions: (context, event) => event.directions,
      }),
      setBins: assign({
        bins: (context, event) => event.bins,
      }),
      setUnits: assign({
        binUnits: (context, event) => event.binUnits,
      }),
      setBinsTitle: assign({
        binsTitle: (context, event) => event.binsTitle,
      }),
      setYUnits: assign({
        yUnits: (context, event) => event.yUnits,
      }),
      makeTableData: assign({
        tableData: (context, { bins, directions }) =>
          makeTableData({ bins, directions, empty: true }),
      }),
      makeTableColumns: assign({
        tableColumns: (context, { bins }) => makeTableColumns(bins),
      }),
      setTableData: assign({
        tableData: (context, event) => event.tableData,
      }),
      // for these data is sent by child machine in event.data ¯\_(ツ)_/¯
      setDirectionsData: assign({
        directions: (context, event) => event.data.directions,
      }),
      setBinsData: assign({
        bins: (context, event) => event.data.bins,
      }),
      makeTableDataAndColumnsData: assign({
        tableColumns: (context, event) => makeTableColumns(event.data.bins),
        tableData: (context, event) =>
          makeTableData({
            bins: event.data.bins,
            directions: event.data.directions,
            empty: true,
          }),
      }),
      // on entry of useExample: setBins, setTableDataAndColumns, setUnits etc,
      // but how to pass data into action there instead of predefining here?
      setDemoData: assign({
        bins: demoBins,
        binsTitle: "Wind speed",
        binUnits: "m/s",
        yUnits: "Hours",
        tableColumns: makeTableColumns(demoBins),
        tableData: makeTableData({
          bins: demoBins,
          directions: demoDirections,
        }),
      }),
      downloadWindroseSvg: () => {
        const windRoseElement = document.getElementById("windrose");
        downloadSvg(windRoseElement, "www.WindRose.xyz", {
          css: "none",
        });
        fetch(`/.netlify/functions/register-download?format=svg`);
      },
      downloadWindrosePng: () => {
        const windRoseElement = document.getElementById("windrose");
        downloadPng(windRoseElement, "www.WindRose.xyz");
        fetch(`/.netlify/functions/register-download?format=png`);
      },
    },
  }
);

export { windroseDataMachine, defineOwnDataMachine };
